import {getVideoAccessNow} from "@/composables/videoAccessNow";
import {computed} from "vue";
import useUserSafe from "@/composables/useUserSafe";

const QUALIFYING_ROLES = [
  "participant", "organizer", "speaker", "sponsor", "session-chair", "press",
];
const IN_PERSON_EVENT_TYPES = ["workshop", "masterclass", "showcase"];

// publicMetadata.events: {"masterclass-2026": {"in-person": [roles], "online": [roles]}, ...}
function hasQualifyingRole(userEvents, eventName, delivery) {
  const roles = userEvents?.[eventName]?.[delivery];
  if (!Array.isArray(roles)) return false;
  return roles.some((role) => QUALIFYING_ROLES.includes(role));
}

// 2-for-1: a colleague paid for this online event, but the registration isn't complete yet
function hasPendingReservation(userEvents, eventName) {
  const roles = userEvents?.[eventName]?.online;
  if (!Array.isArray(roles) || !roles.includes("reserved")) return false;
  return !hasQualifyingRole(userEvents, eventName, "online");
}

function getUserEvents(user) {
  return user?.publicMetadata?.events || null;
}

function getHasFullAccess(user, year) {
  const videoAccess = getVideoAccessNow(user?.publicMetadata?.videoAccess);
  if ((videoAccess.workshopVideosUntil || 0) < year) return false;
  return (videoAccess.masterclassVideosUntil || 0) >= year;
}

export default function useEventRegistration(year) {
  const {isSignedIn, user} = useUserSafe();
  function isRegisteredOnline(eventType) {
    if (!isSignedIn.value || !eventType) return false;
    return hasQualifyingRole(getUserEvents(user.value), `${eventType}-${year}`, "online");
  }
  function isReservedOnline(eventType) {
    if (!isSignedIn.value || !eventType) return false;
    return hasPendingReservation(getUserEvents(user.value), `${eventType}-${year}`);
  }
  const isRegisteredInPerson = computed(() => {
    if (!isSignedIn.value) return false;
    const userEvents = getUserEvents(user.value);
    return IN_PERSON_EVENT_TYPES.some((eventType) =>
      hasQualifyingRole(userEvents, `${eventType}-${year}`, "in-person"));
  });
  const hasFullAccess = computed(() => {
    if (!isSignedIn.value) return false;
    return getHasFullAccess(user.value, Number(year));
  });
  return {isSignedIn, isRegisteredOnline, isReservedOnline, isRegisteredInPerson, hasFullAccess};
}
